import { useTranslation } from 'react-i18next'
import { MapPin, Phone, Mail, ArrowUpRight } from 'lucide-react'
import { LocalizedLink } from '@/components/LocalizedLink'
import { localizedRoutes, type RouteKey } from '@/i18n/routes'

const mainNavKeys: { key: RouteKey; labelKey: string }[] = [
  { key: 'home', labelKey: 'nav.home' },
  { key: 'about', labelKey: 'nav.about' },
  { key: 'services', labelKey: 'nav.services' },
  { key: 'machines', labelKey: 'nav.machines' },
  { key: 'news', labelKey: 'nav.news' },
  { key: 'events', labelKey: 'nav.events' },
  { key: 'partners', labelKey: 'nav.partners' },
  { key: 'contacts', labelKey: 'nav.contacts' },
]

const legalKeys: { key: RouteKey; labelKey: string }[] = [
  { key: 'privacy', labelKey: 'footer.privacy' },
  { key: 'cookiePolicy', labelKey: 'footer.cookiePolicy' },
  { key: 'legalNotice', labelKey: 'footer.legalNotes' },
]

/**
 * Footer — the dark industrial footer that sits below every page.
 *
 * Design: near-black (ink-900) background, brand-blue accent stripe at the
 * top, 4-column desktop grid (brand/contact + nav + legal + hours/extras),
 * collapsing to a 2-column tablet layout and single-column mobile. Logo
 * shown on a light disk so its dark linework remains readable. Fine
 * rule-lines between column headers mimic an engineering title block.
 */
export function Footer() {
  const { t, i18n } = useTranslation()
  const currentYear = new Date().getFullYear()
  const companyName = t('meta.companyName')

  // Use translated contact fields; fall back to nothing if empty
  const address = t('contact.address')
  const city = t('contact.city')
  const email = t('contact.email')
  const phone = t('contact.phone')
  const vat = t('contact.vat')
  const tagline = t('footer.tagline')

  const localeLang = (i18n.language || 'it') as 'it' | 'en'
  const localizedHomeHref = `/${localeLang}${localizedRoutes.home[localeLang] ? `/${localizedRoutes.home[localeLang]}` : ''}`

  return (
    <footer
      className="relative bg-ink-900 text-white/85"
      aria-labelledby="site-footer-title"
    >
      {/* Top brand accent */}
      <div
        aria-hidden="true"
        className="h-1 w-full bg-gradient-to-r from-brand-500 via-brand-600 to-brand-500"
      />

      {/* Corner engineering marks */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute top-6 right-6 flex gap-1 text-brand-400 opacity-60"
      >
        <span className="block h-4 w-px bg-current" />
        <span className="block h-2 w-px bg-current" />
        <span className="block h-4 w-px bg-current" />
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-16 pb-10">
        <h2 id="site-footer-title" className="sr-only">
          {t('footer.srTitle', { defaultValue: 'Site footer' })}
        </h2>

        <div className="grid grid-cols-1 gap-10 md:grid-cols-2 lg:grid-cols-12">
          {/* Brand + tagline + contact */}
          <div className="lg:col-span-5">
            <LocalizedLink
              to="home"
              className="inline-flex items-center bg-white p-3 rounded-sm hover:bg-ink-100 transition-colors"
              aria-label={t('aria.homeLink')}
            >
              <img
                src="/images/logo.png"
                alt={t('aria.logoAlt')}
                className="h-9 w-auto"
                draggable={false}
              />
            </LocalizedLink>

            {tagline && (
              <p className="mt-5 max-w-sm text-[15px] leading-relaxed text-white/70 fx-text-pretty">
                {tagline}
              </p>
            )}

            <address className="mt-6 not-italic space-y-3 text-[15px] text-white/80">
              {(address || city) && (
                <div className="flex gap-3">
                  <MapPin
                    className="mt-0.5 h-4 w-4 flex-shrink-0 text-brand-400"
                    strokeWidth={2.25}
                    aria-hidden="true"
                  />
                  <div className="leading-relaxed">
                    {address && <div>{address}</div>}
                    {city && <div>{city}</div>}
                  </div>
                </div>
              )}
              {phone && (
                <div className="flex gap-3">
                  <Phone
                    className="mt-0.5 h-4 w-4 flex-shrink-0 text-brand-400"
                    strokeWidth={2.25}
                    aria-hidden="true"
                  />
                  <a
                    href={`tel:${phone.replace(/\s+/g, '')}`}
                    className="hover:text-white transition-colors"
                  >
                    {phone}
                  </a>
                </div>
              )}
              {email && (
                <div className="flex gap-3">
                  <Mail
                    className="mt-0.5 h-4 w-4 flex-shrink-0 text-brand-400"
                    strokeWidth={2.25}
                    aria-hidden="true"
                  />
                  <a
                    href={`mailto:${email}`}
                    className="hover:text-white transition-colors break-all"
                  >
                    {email}
                  </a>
                </div>
              )}
              {vat && (
                <div className="pl-7 text-xs text-white/55">
                  {t('footer.vatLabel')}: {vat}
                </div>
              )}
            </address>
          </div>

          {/* Main nav */}
          <div className="lg:col-span-4">
            <FooterColumnHeading>{t('footer.navigation')}</FooterColumnHeading>
            <ul className="mt-5 grid grid-cols-2 gap-x-6 gap-y-2.5 text-[15px]">
              {mainNavKeys.map(({ key, labelKey }) => (
                <li key={key}>
                  <LocalizedLink
                    to={key}
                    className="group inline-flex items-center gap-1.5 text-white/75 hover:text-brand-400 transition-colors"
                  >
                    <span
                      aria-hidden="true"
                      className="block h-px w-2 bg-brand-500 transition-all duration-200 group-hover:w-4"
                    />
                    {t(labelKey)}
                  </LocalizedLink>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div className="lg:col-span-3">
            <FooterColumnHeading>{t('footer.legal')}</FooterColumnHeading>
            <ul className="mt-5 space-y-2.5 text-[15px]">
              {legalKeys.map(({ key, labelKey }) => (
                <li key={key}>
                  <LocalizedLink
                    to={key}
                    className="text-white/75 hover:text-brand-400 transition-colors"
                  >
                    {t(labelKey)}
                  </LocalizedLink>
                </li>
              ))}
            </ul>

            {/* Secondary CTA */}
            <LocalizedLink
              to="contacts"
              className="mt-8 inline-flex items-center gap-2 rounded-sm border border-brand-500 px-4 py-2.5 text-sm font-semibold text-brand-400 transition-colors hover:bg-brand-500 hover:text-white"
            >
              {t('buttons.contactUs')}
              <ArrowUpRight className="h-4 w-4" strokeWidth={2.5} aria-hidden="true" />
            </LocalizedLink>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-14 border-t border-white/10 pt-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-[13px] text-white/55">
          <p>
            © {currentYear} {companyName}. {t('footer.allRightsReserved')}
          </p>
          <p className="font-mono tracking-wider uppercase text-[11px]">
            <a
              href={localizedHomeHref}
              className="hover:text-brand-400 transition-colors"
            >
              {t('footer.website')}
            </a>
          </p>
        </div>
      </div>
    </footer>
  )
}

function FooterColumnHeading({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="relative text-[11px] font-bold uppercase tracking-[0.22em] text-white pb-3 border-b border-white/10">
      <span className="absolute bottom-[-1px] left-0 block h-px w-10 bg-brand-500" />
      {children}
    </h3>
  )
}
